import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import Page from '../default-v2/Page';
import Breadcrumb from '../../components/Breadcrumbs';
import intl from 'react-intl-universal';
import userImg from '../../assets/img/users.svg';
import CardHeader from '../default-v2/CardHeader';
import Axios from '../../config/Axios';
import imgfSelectDown from '../default-v2/img/select-down.svg';
import imgfSelectUp from '../default-v2/img/select-up.svg';
import { can } from '../../config/Permissions';
import { formatDateToUserTZ } from '../../utils/time';
import oClasses from '../operational-panel/MigrateResponsibilityOperational.module.scss'; // usa o mesmo css
import { ResponsibilityMigrationPageHeader, SelectResponsibleModal } from '../operational-panel/MigrateResponsibilityOperational';

const MigrateResponsibilityAudit = () => {
  const [oConfig, setConfig] = useState(
    JSON.parse(localStorage.getItem('cConfig')) || { cOrderBy: 'cName', cType: 'asc' }
  );
  const [cSearch, setSearch] = useState('');

  const [oMigrateModalOptions, setMigrateModalOptions] = useState({ bShow: false });
  const [bIsLoading, setIsLoading] = useState(true);
  const refIsFirstLoad = useRef(true);

  const [bHasNextPage, setHasNextPage] = useState(true);
  const [nNextPage, setNextPage] = useState(1);
  const [aUsers, setUsers] = useState([]);
  const [aResponsibilities, setResponsibilities] = useState([]);
  const [aSelectedResps, setSelectedResps] = useState([]);
  const oParams = useParams();

  const oCheckAllCbRef = useRef(null);
  const oObserverRef = useRef(null);
  const fnLastItemRef = useCallback((elLastItem) => {
    if (bIsLoading) return;
    if (oObserverRef.current) {
      oObserverRef.current.disconnect();
    }
    if (!bHasNextPage) return;

    oObserverRef.current = new IntersectionObserver(([oEntry]) => {
      if (!oEntry.isIntersecting) return;
      getResponsibilities();
    }, {
      threshold: 0,
      rootMargin: '0px 200px 0px 200px',
    });

    if (elLastItem) oObserverRef.current.observe(elLastItem);
  }, [bIsLoading, bHasNextPage]);

  const nUserId = +oParams.nId;

  const resetReponsibilityList = () => {
    getResponsibilities(true);
    setSelectedResps([]);
    uncheckCheckAllCb();
  }

  // atualiza a lista quando altera a ordenação
  useEffect(() => {
    if (bIsLoading && !refIsFirstLoad.current) return;
    refIsFirstLoad.current = false;

    if (bHasNextPage) {
      resetReponsibilityList();
      return;
    }
    const aNewResps = [...aResponsibilities];
    sortResponsibilities(aNewResps, oConfig.cOrderBy, oConfig.cType === 'asc');
    setResponsibilities(aNewResps);
  }, [oConfig.cOrderBy, oConfig.cType]);

  useEffect(() => {
    if (bIsLoading) return;
    resetReponsibilityList();
  }, [cSearch]);

  useEffect(() => {
    if (!can('admin')) {
      window.location.href = '/';
      return;
    }
    Axios.get(`/user/all`).then((oResponse) => {
      const aTargetUsers = oResponse.data.aUsers
        .filter(item => item.user_nid !== nUserId)
        .map(oUser => ({ value: oUser.user_nid, label: oUser.user_cname }));
      setUsers(aTargetUsers);
    });
  }, []);

  const getResponsibilities = async (bResetList = false) => {
    try {
      setIsLoading(true);

      const { data } = await Axios.get(`/audit/get-responsibilities/${nUserId}`, {
        params: {
          orderBy: oConfig.cOrderBy,
          orderDir: oConfig.cType,
          page: bResetList ? 1 : nNextPage,
          ...(!!cSearch && { search: cSearch }),
        },
      });
      /** @type {AuditResponsibility[]} */
      const aNewResponsibilities = data.aResponsibilities;
      const bHasMore = data.bHasNextPage;

      setHasNextPage(bHasMore);
      if (bHasMore) {
        setNextPage((nCurrPage) => bResetList ? 2 : nCurrPage + 1);
      }
      setResponsibilities((aResponsibilities) => bResetList
        ? aNewResponsibilities
        : [...aResponsibilities, ...aNewResponsibilities]
      );
      uncheckCheckAllCb();
    } catch (oError) {
      //
    } finally {
      setIsLoading(false);
    }
  };

  const migrateResponsibilities = async (nNewUser, cPassword, bIsMigrateAll) => {

    // otimização simples para reduzir tamanho da requisição e acelerar a migração:
    // quando todas as responsabilidades forem selecionadas, trata como migrar todas
    const bMigrateAll = bIsMigrateAll
      || (!cSearch && !bHasNextPage && aSelectedResps.length === aResponsibilities.length);

    await Axios.post(`/audit/migrate-responsibilities/${nUserId}`, {
      nNewUser,
      cPassword,
      aAuditIds: !bMigrateAll ? aSelectedResps : [],
      bMigrateAll,
    });
    setMigrateModalOptions(() => ({ bShow: false }));
    getResponsibilities(true);
    setSelectedResps([]);
    /*
    toast({
      type: 'success',
      title: intl.get('FolderIndex.success_migration'),
    });
    */
  };

  const updateSorting = (cColumn) => {
    setConfig((oPrevConfig) => {
      const oNewConfig = {
        ...oPrevConfig,
        ...(oConfig.cOrderBy !== cColumn
          ? { cOrderBy: cColumn }
          : { cType: oPrevConfig.cType === 'asc' ? 'desc' : 'asc' }
        ),
      };
      localStorage.setItem('cConfig', JSON.stringify(oNewConfig));
      return oNewConfig;
    });
  };

  const handleOpenMigrateModal = (bMigrateAll = false) => {
    setMigrateModalOptions((oPrevOptions) => ({
      ...oPrevOptions,
      bShow: true,
      bMigrateAll,
    }));
  };

  const handleCloseMigrateModal = () => {
    setMigrateModalOptions(() => ({ bShow: false }));
  }

  const handleCheckAll = useCallback((evtClick) => {
    const bIsChecked = evtClick.target.checked;
    const aNewSelectedResps = bIsChecked ? aResponsibilities.map(({ nId }) => nId) : [];
    setSelectedResps(aNewSelectedResps);
  }, [aResponsibilities, setSelectedResps]);

  const uncheckCheckAllCb = () => {
    const elCheckAllCb = oCheckAllCbRef.current;
    if (elCheckAllCb) elCheckAllCb.checked = false;
  }

  const handleResponsibilityCheck = (evtClick, oResponsibility) => {
    const bIsChecked = evtClick.target.checked;
    const { nId } = oResponsibility;
    if (bIsChecked) {
      setSelectedResps((nIds) => [...nIds, nId]);
    } else {
      setSelectedResps((cIdxs) => cIdxs.filter((cIdxs) => cIdxs !== nId));
      uncheckCheckAllCb();
    }
  }

  const aPages = [
    { cName: 'user', cLink: '/user' },
    { cName: 'User.audits', cLink: '/audit-management' },
    { cName: 'User.migrate_responsibilities' }
  ];
  return (
    <Page
      loading={0}
      rcmpBreadcrumb={<Breadcrumb aPages={aPages} />}
      cTitle={intl.get('User.migrate_responsibilities')}
      cImage={userImg}
      rcmpCardHeader={<ResponsibilityMigrationPageHeader onSearchChange={setSearch} />}
      className="v2-document"
      cCurrentSideMenuOption={intl.get('Nav.users')}
    >
      <div style={{ height: '64vh', overflowY: 'auto' }}>
        <ResponsibilitiesListHeader
          onCheckAll={handleCheckAll}
          onUpdateOrder={updateSorting}
          oConfig={oConfig}
          oCheckAllCbRef={oCheckAllCbRef}
        />
        <ul style={{ display: 'block' }} className="document-list list-shadow list-view">
          {aResponsibilities.map((oResponsibility, nIdx) => (
            <ResponsibilitiesListItem
              key={oResponsibility.nId}
              ref={nIdx + 1 === aResponsibilities.length ? fnLastItemRef : null}
              oResponsibility={oResponsibility}
              onCheck={handleResponsibilityCheck}
              bIsChecked={aSelectedResps.includes(oResponsibility.nId)}
              nUserId={nUserId}
            />
          ))}
          {bIsLoading && (
            <li className="centered-text">
              <div className="spinner" style={{ width: '50px', height: '50px' }} />
              <br />
              {intl.get('carregando')}
            </li>
          )}
        </ul>
      </div>
      <div className="button-stack">
        <Button
          type="button"
          className="btn break-line-btn"
          onClick={() => handleOpenMigrateModal(true)}
        >
          {intl.get('FolderIndex.migrate')} <br /> {intl.get('User.all')}
        </Button>
        <Button
          type="button"
          className="btn break-line-btn"
          disabled={aSelectedResps.length < 1}
          onClick={() => handleOpenMigrateModal()}
        >
          {intl.get('FolderIndex.migrate')} <br /> {intl.get('FolderIndex.selecteds')}
        </Button>
      </div>
      {oMigrateModalOptions.bShow && (
        <SelectResponsibleModal
          onClose={handleCloseMigrateModal}
          aUsers={aUsers}
          onSubmit={migrateResponsibilities}
          bMigrateAll={oMigrateModalOptions.bMigrateAll}
        />
      )}
    </Page>
  );
};

const ResponsibilitiesListHeader = ({
  onCheckAll = () => { },
  onUpdateOrder = () => { },
  oConfig = {},
  oCheckAllCbRef,
}) => {
  const { cOrderBy, cType: cOrderType } = oConfig;
  const cIcon = cOrderType === 'asc' ? imgfSelectUp : imgfSelectDown;

  return (
    <div className={classNames("filterList", "noselect", oClasses['migrate-list'])}>
      <span role="button" tabIndex="0" className="alt-checkbox-v2">
        <input type="checkbox" onChange={onCheckAll} ref={oCheckAllCbRef} />
      </span>{' '}

      <span
        role="button"
        tabIndex="0"
        onClick={() => onUpdateOrder('cNorm')}
        className={classNames('filterByName-v2', cOrderBy === 'cNorm' && 'active')}
      >
        {intl.get('Audit.norma_label')}
        {cOrderBy === 'cNorm' && (
          <img className="folderindex-list-icon" src={cIcon} alt={cOrderType} />
        )}
      </span>

      <span className="filterByPublish" style={{ cursor: 'default' }}>
        {intl.get('FolderIndex.responsibilities')}
      </span>

      <span
        role="button"
        tabIndex="0"
        onClick={() => onUpdateOrder('cLocal')}
        className={classNames('filterByPublish', cOrderBy === 'cLocal' && 'active')}
      >
        {intl.get('Audit.audit_location')}
        {cOrderBy === 'cLocal' && (
          <img className="folderindex-list-icon" src={cIcon} alt={cOrderType} />
        )}
      </span>

      <span
        role="button"
        tabIndex="0"
        onClick={() => onUpdateOrder('bIsSingleResponsible')}
        className={classNames('filterByPublish', cOrderBy === 'bIsSingleResponsible' && 'active')}
        style={{ width: '21%' }}
      >
        {intl.get('PermissionGroup.sole_responsible')}
        {cOrderBy === 'bIsSingleResponsible' && (
          <img className="folderindex-list-icon" src={cIcon} alt={cOrderType} />
        )}
      </span>

      <span
        role="button"
        tabIndex="0"
        className="filterByPublish"
        style={{ width: '10%', cursor: 'default' }}
      >
        {intl.get('Audit.periodo_label')}
      </span>
    </div>
  );
};

const ResponsibilitiesListItem = forwardRef(({
  oResponsibility,
  onCheck = () => { },
  bIsChecked,
  nUserId
}, ref) => {

  const bIsSingleRespAndAuditor = oResponsibility?.bIsSingleResponsible && oResponsibility?.bIsSingleAuditor;

  const getIsOnlyResponsibleHint = () => {
    const { bIsSingleResponsible, bIsSingleAuditor } = oResponsibility;
    if (!bIsSingleResponsible && !bIsSingleAuditor) return;

    if (bIsSingleRespAndAuditor) {
      return intl.get('MigrationBoard.unique_migration_hint');
    }
    return intl.get('MigrationBoard.not_unique_migration_hint', {
      cRole: bIsSingleResponsible ? intl.get('responsible') : intl.get('DashboardAudit.auditor'),
    });
  }
  return (
    <li role="presentation" ref={ref}>
      <h2 className="alt-checkbox-v2 ">
        <input
          type="checkbox"
          onChange={(evtChange) => onCheck(evtChange, oResponsibility)}
          checked={bIsChecked}
        />
      </h2>

      <h2 className="filterByName-v2" title={oResponsibility.cNorm}>
        {oResponsibility.cNorm}
      </h2>

      <p className="filterByPublish">
        <ResponsibilitiesColumn oResponsibility={oResponsibility} nUserId={nUserId} />
      </p>

      <p className="filterByPublish">
        {oResponsibility.cLocal}
      </p>

      <p
        className="filterByPublish"
        style={{ width: '17%' }}
        title={getIsOnlyResponsibleHint()}
      >
        {bIsSingleRespAndAuditor ? intl.get('sim') : intl.get('nao')}
      </p>

      <p className="filterByExecutionDate">
        {formatDateToUserTZ(oResponsibility.dtBegin, 'LL')}
        {' - '}
        {formatDateToUserTZ(oResponsibility.dtEnd, 'LL')}
      </p>
    </li>
  )
});

const ResponsibilitiesColumn = ({ oResponsibility = {}, nUserId }) => {
  const aResponsibilities = useMemo(() => {
    const aLabels = [];

    const bIsResponsible = oResponsibility.bIsResponsible
    const bIsAuditor = oResponsibility.bIsAuditor
    if (bIsResponsible) {
      aLabels.push(intl.get('responsible'));
    }
    if (bIsAuditor) {
      aLabels.push(intl.get('DashboardAudit.auditor'));
    }
    return aLabels;
  }, [oResponsibility]);

  return (
    <span style={{ textWrap: 'nowrap' }}>
      {aResponsibilities.join(' / ')}
    </span>
  );
};

/**
 * Ordena e retorna a lista de responsabilidades. **Modifica o array passado como parâmetro.**
 * @param {AuditResponsibility[]} aResponsibilities
 * @param {"cNorm"|"cLocal"|"cResponsibilities"|"bIsSingleResponsible"|"dtBegin"} cColumn
 * @param {boolean} bAscending
 */
const sortResponsibilities = (aResponsibilities, cColumn, bAscending) => {
  const nSign = bAscending ? 1 : -1;

  if (cColumn === 'bIsSingleResponsible') {
    return aResponsibilities.sort((a, b) => {
      const bIsSingleRespAndAuditorA = a.bIsSingleResponsible + a.bIsSingleAuditor;
      const bIsSingleRespAndAuditorB = b.bIsSingleResponsible + b.bIsSingleAuditor;
      return nSign * (bIsSingleRespAndAuditorA - bIsSingleRespAndAuditorB);
    });
  }
  return aResponsibilities.sort((a, b) => nSign * (a[cColumn] ?? '').localeCompare(b[cColumn] ?? ''));
};

/**
 * @typedef {object} AuditResponsibility
 *
 * @property {number} nId
 * @property {string} cNorm
 * @property {string} cLocal
 * @property {boolean} bIsAuditor
 * @property {boolean} bIsResponsible
 * @property {boolean} bIsSingleResponsible
 * @property {boolean} bIsSingleAuditor
 * @property {string} dtBegin
 * @property {string} dtEnd
 */


export default MigrateResponsibilityAudit;
